/**
 * Compression cote telephone AVANT l'envoi : 1600 px de large maximum, WebP,
 * ~200 Ko par photo au lieu de 4 Mo.
 *
 * Ce n'est pas un detail : 12 photos brutes par location remplissent les 80 Go
 * du serveur en moins de deux ans. En compressant ici, on descend a ~3 Mo par
 * location, le disque tient des annees, et le VPS ne fait aucun traitement d'image.
 */
import { urlApi } from '../api/config';
import { T } from '../i18n';

export const LARGEUR_MAX = 1600;
export const QUALITE = 0.72;

export async function compresser(fichier: File): Promise<Blob> {
  const bitmap = await createImageBitmap(fichier);
  const ratio = Math.min(1, LARGEUR_MAX / Math.max(bitmap.width, bitmap.height));
  const l = Math.round(bitmap.width * ratio);
  const h = Math.round(bitmap.height * ratio);

  const canvas = document.createElement('canvas');
  canvas.width = l; canvas.height = h;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, l, h);
  bitmap.close();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(T.photosEdl.compressionImpossible))),
      'image/webp', QUALITE,
    );
  });
}

export const blobEnDataUrl = (b: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(b);
  });

export const dataUrlEnBlob = async (d: string): Promise<Blob> => (await fetch(d)).blob();

/**
 * Envoie les photos au serveur, **une par une**.
 *
 * Ce n'est pas un détail. L'API passe par Cloudflare, qui coupe toute requête
 * dont l'écriture vers le serveur dépasse 30 secondes — un plafond non
 * modifiable, sur tous les plans. Six photos dans une seule requête, depuis un
 * téléphone en 3G au bord de la route, dépassent ce délai : l'agent reçoit une
 * erreur 524 et perd tout son état des lieux.
 *
 * Une par une : chaque envoi fait ~200 Ko, passe en quelques secondes, et un
 * échec ne coûte qu'une photo au lieu des six.
 */
export async function envoyerPhotos(
  blobs: Blob[],
  token: string | null,
  onProgres?: (faites: number, total: number) => void,
): Promise<string[]> {
  const urls: string[] = [];
  for (const [i, blob] of blobs.entries()) {
    const form = new FormData();
    form.append('fichiers', blob, `photo-${i}.webp`);

    let derniere: unknown;
    // Réseau instable sur le parking : on retente deux fois avant d'abandonner.
    for (let essai = 0; essai < 3; essai++) {
      try {
        const r = await fetch(urlApi('/api/fichiers'), {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        });
        if (r.status === 413) throw new Error(T.photosEdl.tropLourde);
        if (r.status === 524 || r.status === 522) throw new Error(T.photosEdl.tropLent);
        if (!r.ok) throw new Error(T.photosEdl.envoiImpossibleCode(r.status));
        urls.push(...(await r.json() as { urls: string[] }).urls);
        derniere = null;
        break;
      } catch (e) {
        derniere = e;
        await new Promise((r) => setTimeout(r, 800 * (essai + 1)));
      }
    }
    if (derniere) throw derniere instanceof Error ? derniere : new Error(T.photosEdl.envoiPhotosImpossible);
    onProgres?.(i + 1, blobs.length);
  }
  return urls;
}

/** Les six prises de vue du contrat, dans l'ordre ou on les demande a l'agent. */
const CLES_VUES = ['avant', 'arriere', 'cote_gauche', 'cote_droit', 'interieur', 'compteur'] as const;
/** Fonction et non constante : le libellé suit la langue active. */
export const vues = () => CLES_VUES.map((cle) => ({ cle, label: T.photosEdl.vues[cle] }));

